//--------------------------------------------------------------------//
//******************** trunkSmsTab **************************************//
//--------------------------------------------------------------------//
// Displays and manages a trunk's optional embedded SMS configuration:
// per-provider credentials (inline edit/save/cancel per field, mirroring
// trunk-info.tab.js), and an enabled toggle that saves immediately on
// change. Ported from the old, standalone SMS settings UI (formerly
// setting-info.tab.js under voip_telephony/sms/) now that SMS lives as an
// embedded sms sub-object on the trunk rather than its own independent
// resource - see sip-gudhub-server's docs/sip-trunks/sms.md. Provider
// itself is a plain top-level trunk field, edited on the Trunk Info tab
// (see trunk-info.tab.js's saveProvider) - this tab reads trunk.provider
// directly to decide which credential fields to show, since both tabs share
// the same trunk object. No separate admins list here: sms is governed by
// the trunk's own admins (see trunk-info.tab.js), not its own.
//
// sms.* saves use Mongo dot-path keys ('sms.enabled', 'sms.credentials')
// rather than a nested {sms: {...}} object, so saving one field (e.g.
// toggling enabled) never clobbers a sibling (e.g. credentials) - see
// docs/sip-trunks/update-trunk.md.
import trunkSmsTemplateUrl from './trunk-sms.template.html';
import { GUDHUB_SETTINGS } from '../../../../config.js';

angular.module('sipTrunksTableModule')

.directive('trunkSmsTab', ['SipTrunksService', 'GhDialog', function (SipTrunksService, GhDialog) {
    return {
        restrict: 'E',
        scope: {
            trunk: '=',
            onUpdate: '&'
        },
        controller: ['$scope', '$element', '$animate', 'localizationService', function ($scope, $element, $animate, localizationService) {
            const dictionary = localizationService.getDictionary('account_settings');
            $scope.translate = (word) => localizationService.translate(word, dictionary);

            // Same ngAnimate ng-if-swap workaround as trunk-info.tab.js.
            $animate.enabled(false, $element);

            // A trunk that has never had SMS configured has no sms key at
            // all - default it locally (not yet saved) so the form has
            // something to bind to.
            $scope.trunk.sms = $scope.trunk.sms || { credentials: {}, enabled: false };
            $scope.trunk.sms.credentials = $scope.trunk.sms.credentials || {};

            const safeApply = function () {
                $scope.$applyAsync();
            };

            // Read-only reference value - the URL this trunk's SMS provider
            // should be configured to push inbound messages to. Valid as
            // soon as trunk.provider is set - trunk_id already exists the
            // moment a trunk is created, so there's no separate minted id to
            // wait on (see sip-gudhub-server's docs/sip-trunks/webhooks.md).
            function updateWebhookUrl() {
                $scope.webhookUrl = $scope.trunk.provider
                    ? GUDHUB_SETTINGS.sip_server_url + '/sms-webhooks/' + $scope.trunk.provider + '/' + $scope.trunk.trunk_id
                    : null;
            }
            updateWebhookUrl();
            $scope.copiedWebhookUrl = false;

            // Mirrors trunk-info.tab.js's copySipServerUri.
            $scope.copyWebhookUrl = async function (event) {
                if (event) {
                    event.preventDefault();
                    event.stopPropagation();
                }

                try {
                    if (navigator.clipboard && navigator.clipboard.writeText) {
                        await navigator.clipboard.writeText($scope.webhookUrl);
                    } else {
                        const textarea = document.createElement('textarea');

                        textarea.value = $scope.webhookUrl;
                        textarea.setAttribute('readonly', '');
                        textarea.style.position = 'absolute';
                        textarea.style.left = '-9999px';

                        document.body.appendChild(textarea);
                        textarea.select();
                        document.execCommand('copy');
                        document.body.removeChild(textarea);
                    }

                    $scope.copiedWebhookUrl = true;
                    safeApply();

                    setTimeout(function () {
                        $scope.copiedWebhookUrl = false;
                        safeApply();
                    }, 1500);
                } catch (error) {
                    console.log('Webhook URL copy error:', error);
                }
            };

            $scope.editMode = {
                account_sid: false,
                auth_token: false,
                api_key: false,
                api_secret: false
            };

            $scope.originalValues = {
                api_key: $scope.trunk.sms.credentials.api_key || '',
                api_secret: $scope.trunk.sms.credentials.api_secret || '',
                account_sid: $scope.trunk.sms.credentials.account_sid || '',
                auth_token: $scope.trunk.sms.credentials.auth_token || ''
            };

            $scope.savingToggle = {
                enabled: false
            };

            // Whether each secret-like credential field currently shows its
            // real value or is masked - same reveal-on-demand pattern as the
            // old setting-info.tab.js. GET /sip-trunks/:id always returns
            // sms.credentials fully masked ("********"); the real value only
            // ever comes from GET /sip-trunks/:id/sms/credentials, fetched
            // here on first reveal/edit.
            $scope.revealed = {
                api_key: false,
                api_secret: false,
                auth_token: false
            };

            async function ensureRealCredential(field) {
                if ($scope.revealed[field]) return;
                const { credentials } = await SipTrunksService.getSmsCredentials($scope.trunk.trunk_id);
                $scope.trunk.sms.credentials[field] = credentials[field];
                $scope.revealed[field] = true;
            }

            $scope.toggleReveal = async function (field) {
                if ($scope.revealed[field]) {
                    $scope.revealed[field] = false;
                    return;
                }

                try {
                    await ensureRealCredential(field);
                    safeApply();
                } catch (error) {
                    console.log('Reveal credential error:', error);
                    showError(error.message || 'Failed to reveal credential');
                }
            };

            function showError(message) {
                GhDialog.show({
                    position: 'center',
                    toolbar: false,
                    template: {
                        toolbar: '',
                        resizable: false,
                        content: `
                            <div class="delete_sip_trunk-popup">
                                <div class="dialog-share-group">
                                    <p class="dialog-share-group__title">Error</p>
                                </div>
                                <div class="dialog-share-group__content">
                                    <p class="info">{{errorMessage}}</p>
                                    <div class="dialog-share-group__content__buttons">
                                        <button class="btn btn-blue" ng-click="cancel()">OK</button>
                                    </div>
                                </div>
                            </div>
                        `
                    },
                    locals: { errorMessage: message },
                    controller: ['$scope', 'errorMessage', function ($scope, errorMessage) {
                        $scope.errorMessage = errorMessage;
                    }]
                });
            }

            // Merges a server response back into trunk, except admins - same
            // reasoning as trunk-info.tab.js's applyTrunkUpdate: trunk-info's
            // own trunk.admins watch would otherwise re-fire spuriously from
            // a save made here, since all tabs share the same trunk object.
            function applyTrunkUpdate(updated) {
                const { admins: _ignoredAdmins, ...rest } = updated;
                Object.assign($scope.trunk, rest);
                updateWebhookUrl();
            }

            // Same edit/save/cancel-per-field dance as trunk-info.tab.js's
            // credential-less fields, but for a nested sms.credentials[field]
            // - always ensures the REAL value is loaded before editing starts,
            // since editing straight from the masked placeholder and saving
            // would silently overwrite the real credential with "********".
            $scope.enableCredentialEdit = async function (field) {
                try {
                    await ensureRealCredential(field);
                    $scope.originalValues[field] = $scope.trunk.sms.credentials[field] || '';
                    $scope.editMode[field] = true;
                    safeApply();
                } catch (error) {
                    console.log('Reveal credential error:', error);
                    showError(error.message || 'Failed to load credential for editing');
                }
            };

            $scope.cancelCredentialEdit = function (field) {
                $scope.trunk.sms.credentials[field] = $scope.originalValues[field];
                $scope.editMode[field] = false;
            };

            // Submits the whole sms.credentials object under the dot-path
            // key 'sms.credentials' - server-side this $set-replaces just
            // that nested object, leaving sms.enabled and every top-level
            // trunk field (including provider) untouched.
            $scope.saveCredentialField = async function (field) {
                try {
                    const updated = await SipTrunksService.update($scope.trunk.trunk_id, { 'sms.credentials': $scope.trunk.sms.credentials });
                    applyTrunkUpdate(updated);
                    $scope.originalValues[field] = $scope.trunk.sms.credentials[field] || '';
                    $scope.editMode[field] = false;
                    safeApply();
                    $scope.onUpdate();
                } catch (error) {
                    console.log('Trunk sms credentials update error:', error);
                    showError(error.message || 'Failed to update credentials');
                }
            };

            // Enabled is a plain checkbox that saves immediately - no
            // edit/save/cancel dance needed for a boolean. Sent as
            // 'sms.enabled' (dot-path), never a nested {sms: {enabled}}
            // object, so it can't clobber sms.credentials.
            $scope.saveToggle = async function (field) {
                $scope.savingToggle[field] = true;

                try {
                    const updated = await SipTrunksService.update($scope.trunk.trunk_id, { ['sms.' + field]: $scope.trunk.sms[field] });
                    applyTrunkUpdate(updated);
                    $scope.onUpdate();
                } catch (error) {
                    console.log('Trunk sms update error:', error);
                    $scope.trunk.sms[field] = !$scope.trunk.sms[field];
                    showError(error.message || 'Failed to update SMS setting');
                } finally {
                    $scope.savingToggle[field] = false;
                    safeApply();
                }
            };
        }],
        templateUrl: trunkSmsTemplateUrl
    };
}]);
